import { Injectable } from '@angular/core';
import { contentTypeSchemaValidatorTextFormat } from 'na-lib';
import { SchemaNode } from '../service/excel-import.service';

type JsonObject = Record<string, unknown>;

export interface JsonSchemaImportResult {
  properties: SchemaNode[];
  warnings: string[];
}

interface ResolvedSchema {
  node: JsonObject;
  stack: string[];
}

@Injectable({ providedIn: 'root' })
export class JsonSchemaImportService {
  import(rootValue: unknown): JsonSchemaImportResult {
    if (!this.isObject(rootValue)) throw new Error('JSON Schema must be an object.');
    const warnings: string[] = [];
    const resolvedRoot = this.resolve(rootValue, rootValue, []);
    const root = resolvedRoot.node;
    if (root['type'] !== 'object' || !this.isObject(root['properties'])) {
      throw new Error('JSON Schema must have an object root with properties.');
    }
    return { properties: this.properties(root, rootValue, '$', warnings, resolvedRoot.stack), warnings };
  }

  private properties(node: JsonObject, root: JsonObject, path: string, warnings: string[], stack: string[]): SchemaNode[] {
    const properties = node['properties'];
    if (!this.isObject(properties)) return [];
    const required = Array.isArray(node['required']) ? node['required'] : [];
    return Object.entries(properties).map(([key, value]) =>
      this.convert(key, value, required.includes(key), root, `${path}.${key}`, warnings, stack)
    );
  }

  private convert(key: string, value: unknown, mandatory: boolean, root: JsonObject, path: string, warnings: string[], stack: string[]): SchemaNode {
    if (!this.isObject(value)) throw new Error(`Unsupported schema at ${path}.`);
    const resolved = this.resolve(value, root, stack);
    const node = resolved.node;
    stack = resolved.stack;
    for (const keyword of ['allOf', 'anyOf', 'oneOf', 'not', 'if', 'then', 'else']) {
      if (node[keyword] !== undefined) throw new Error(`Unsupported schema composition ${keyword} at ${path}.`);
    }
    const rawType = node['type'];
    const type = Array.isArray(rawType) && rawType.length === 2 && rawType.includes('null')
      ? rawType.find(item => item !== 'null') : rawType;
    if (typeof type !== 'string') throw new Error(`Missing or unsupported type at ${path}.`);
    if (!['object', 'array', 'string', 'integer', 'number', 'boolean'].includes(type)) {
      throw new Error(`Unsupported type "${type}" at ${path}.`);
    }

    const schema: SchemaNode = {
      key,
      type: type === 'integer' ? 'number' : type as SchemaNode['type'],
      mandatory
    };
    const description = node['description'] ?? node['title'];
    if (typeof description === 'string') schema.description = description;
    if (node['x-AccessRights'] !== undefined) schema.access = this.access(node['x-AccessRights'], path, warnings);

    const validators = this.validators(node, schema.type, type === 'integer', path, warnings);
    if (validators.length) schema.validators = validators;

    if (type === 'object') {
      schema.properties = this.properties(node, root, path, warnings, stack);
    } else if (type === 'array') {
      const item = node['items'];
      if (item === undefined || item === true) {
        warnings.push(`${path}: array item type is unspecified; imported as string.`);
        schema.items = { key: 'items', type: 'string' };
      } else {
        schema.items = this.convert('items', item, false, root, `${path}[]`, warnings, stack);
      }
    }

    for (const keyword of ['patternProperties', 'additionalProperties', 'dependentSchemas', 'contains', 'prefixItems']) {
      if (node[keyword] !== undefined) warnings.push(`${path}: ${keyword} is not represented by the content schema.`);
    }
    return schema;
  }

  private resolve(value: JsonObject, root: JsonObject, stack: string[]): ResolvedSchema {
    const ref = value['$ref'];
    if (ref === undefined) return { node: value, stack };
    if (typeof ref !== 'string' || !ref.startsWith('#/')) throw new Error(`Unsupported reference: ${String(ref)}.`);
    if (stack.includes(ref)) throw new Error(`Cyclic reference: ${ref}.`);
    const parts = ref.slice(2).split('/').map(part => part.replace(/~1/g, '/').replace(/~0/g, '~'));
    let target: unknown = root;
    for (const part of parts) target = this.isObject(target) ? target[part] : undefined;
    if (!this.isObject(target)) throw new Error(`Reference not found: ${ref}.`);
    const resolved = this.resolve(target, root, [...stack, ref]);
    const overlay = { ...value };
    delete overlay['$ref'];
    const node = { ...resolved.node, ...overlay };
    const baseProperties = resolved.node['properties'];
    const overlayProperties = overlay['properties'];
    if (this.isObject(baseProperties) && this.isObject(overlayProperties)) {
      node['properties'] = { ...baseProperties, ...overlayProperties };
    }
    const baseRequired = resolved.node['required'];
    const overlayRequired = overlay['required'];
    if (Array.isArray(baseRequired) && Array.isArray(overlayRequired)) {
      node['required'] = [...new Set([...baseRequired, ...overlayRequired])];
    }
    return { node, stack: resolved.stack };
  }

  private access(value: unknown, path: string, warnings: string[]): Record<string, string[]> {
    let rights: unknown = value;
    if (typeof rights === 'string') {
      try { rights = JSON.parse(rights); }
      catch { throw new Error(`Invalid x-AccessRights at ${path}.`); }
    }
    if (!this.isObject(rights)) {
      throw new Error(`Invalid x-AccessRights at ${path}.`);
    }
    const read = rights['read'];
    const write = rights['write'];
    if (!Array.isArray(read) || !Array.isArray(write)) {
      throw new Error(`Invalid x-AccessRights at ${path}.`);
    }
    if (![...read, ...write].every(role =>
      typeof role === 'string' && role.trim() && !['__proto__', 'constructor', 'prototype'].includes(role)
    )) {
      throw new Error(`Invalid x-AccessRights roles at ${path}.`);
    }
    const access: Record<string, string[]> = { public: [] };
    for (const role of read) {
      access[role] ||= [];
      if (!access[role].includes('R')) access[role].push('R');
    }
    for (const role of write) {
      if (role === 'public') {
        warnings.push(`${path}: public write access was ignored; public supports read access only.`);
        continue;
      }
      access[role] ||= [];
      if (read.includes('public') && !access[role].includes('R')) access[role].push('R');
      for (const permission of ['C', 'U']) {
        if (!access[role].includes(permission)) access[role].push(permission);
      }
    }
    return access;
  }

  private validators(node: JsonObject, type: SchemaNode['type'], isInteger: boolean, path: string, warnings: string[]): Array<Record<string, unknown>> {
    const validators: Array<Record<string, unknown>> = [];
    if (type === 'string') {
      const min = node['minLength'];
      const max = node['maxLength'];
      if (typeof min === 'number' || typeof max === 'number') {
        validators.push({ type: 'textLength', ...(typeof min === 'number' ? { min } : {}), ...(typeof max === 'number' ? { max } : {}) });
      }
      if (typeof node['pattern'] === 'string') validators.push({ type: 'textRegex', regex: node['pattern'] });
      const format = node['format'];
      if (typeof format === 'string') {
        const supported = Object.values(contentTypeSchemaValidatorTextFormat) as string[];
        if (supported.includes(format)) validators.push({ type: 'textFormat', format });
        else warnings.push(`${path}: format "${format}" is not supported by the field validator.`);
      }
    }
    if (type === 'number') {
      const min = node['exclusiveMinimum'] ?? node['minimum'];
      const max = node['exclusiveMaximum'] ?? node['maximum'];
      const multipleOf = node['multipleOf'];
      if (typeof min === 'number' || typeof max === 'number' || typeof multipleOf === 'number' || isInteger) {
        validators.push({
          type: 'numericRange',
          ...(isInteger ? { decimal: false } : {}),
          ...(typeof min === 'number' ? { min } : {}),
          ...(typeof max === 'number' ? { max } : {}),
          ...(typeof node['exclusiveMinimum'] === 'number' ? { minExclusive: true } : {}),
          ...(typeof node['exclusiveMaximum'] === 'number' ? { maxExclusive: true } : {}),
          ...(typeof multipleOf === 'number' ? { multipleOf } : {})
        });
      }
    }
    const values = Array.isArray(node['enum']) ? node['enum'] : node['const'] !== undefined ? [node['const']] : undefined;
    if (values?.length) {
      if (type === 'string' && values.every(value => typeof value === 'string')) validators.push({ type: 'textEnum', values });
      else if (type === 'number' && values.every(value => typeof value === 'number')) validators.push({ type: 'numericEnum', values });
      else warnings.push(`${path}: enum/const is not supported for this field type.`);
    }
    if (type === 'array') {
      const min = node['minItems'];
      const max = node['maxItems'];
      if (typeof min === 'number' || typeof max === 'number') {
        validators.push({
          type: 'arrayLength',
          ...(typeof min === 'number' ? { min } : {}),
          ...(typeof max === 'number' ? { max } : {})
        });
      }
      if (node['uniqueItems'] === true) warnings.push(`${path}: uniqueItems is not supported by the field validator.`);
    }
    return validators;
  }

  private isObject(value: unknown): value is JsonObject {
    return value !== null && typeof value === 'object' && !Array.isArray(value);
  }
}
